import {
  LocaleEnum,
  Metadata,
  MetadataDocument,
  MetadataQuery,
  MetadataQueryVariables,
  PageEnum,
  Readable,
  ReadableDocument,
  ReadableQuery,
  ReadableQueryVariables,
} from 'generated/apolloComponents'
import { addApolloState, initializeApollo } from '@/api/apollo'
import CustomHead from '@/components/molecules/CustomHead'
import {
  getHrReadableLinkForGraphqlType,
  getReadableLinkForGraphqlType,
} from '@/helpers/utils'
import ThemeSelector from '@/components/templates/Blog/ThemeSelector'

interface IProps {
  readable: Readable
  metaData: Metadata
}

const SingleRead = ({ readable, metaData }: IProps) =>
  readable && (
    <>
      <CustomHead metaData={metaData} />
      <ThemeSelector readable={readable} />
    </>
  )

export const getServerSideProps = async (context: any) => {
  let props = {}
  const apolloClient = initializeApollo({ ctx: context })

  if (context.params?.slug) {
    const readableData = await apolloClient.query<
      ReadableQuery,
      ReadableQueryVariables
    >({
      query: ReadableDocument,
      variables: {
        readableId: context.params?.slug,
        viewToken: context.query?.viewToken,
        language: LocaleEnum[context.locale as 'pl' | 'en'],
      },
      errorPolicy: 'all',
    })

    if (readableData.errors) {
      if (readableData.errors[0].message === 'MOVED_PERMANENTLY') {
        const destination =
          readableData.errors[0].extensions?.redirect || '/readme'
        return {
          redirect: {
            destination: destination,
            permanent: true,
          },
        }
      }
      return {
        notFound: true,
      }
    }

    const currentPath = context.resolvedUrl.split('?')[0]
    const properPath = getReadableLinkForGraphqlType(readableData.data.readable)
    // NEXTAUTH_URL is a current domain basically
    const properUrl = getReadableLinkForGraphqlType(
      readableData.data.readable,
      process.env.NEXTAUTH_URL
    )

    if (currentPath !== properPath) {
      return {
        redirect: {
          destination: properUrl,
          permanent: true,
        },
      }
    }

    if (!readableData.data.readable?.forIt) {
      const properHrUrl = getHrReadableLinkForGraphqlType(
        readableData.data.readable,
        process.env.NEXTAUTH_URL
      )
      return {
        redirect: {
          destination: properHrUrl,
          permanent: true,
        },
      }
    }

    if (
      readableData.data.readable?.language === 'pl' &&
      context.locale === 'en'
    ) {
      return {
        redirect: {
          destination: `https://${process.env.PL_HOST}${properPath}`,
          permanent: true,
        },
      }
    }

    if (
      readableData.data.readable?.language === 'en' &&
      context.locale === 'pl'
    ) {
      return {
        redirect: {
          destination: `https://${process.env.EN_HOST}${properPath}`,
          permanent: true,
        },
      }
    }

    const metaData = await apolloClient.query<
      MetadataQuery,
      MetadataQueryVariables
    >({
      query: MetadataDocument,
      variables: {
        pageType: PageEnum.ReadableDetails,
        language: LocaleEnum[context.locale as 'pl' | 'en'],
        id: readableData.data.readable?.id,
      },
    })

    props = {
      ...props,
      ...{
        readable: readableData.data.readable,
        metaData: metaData.data.metadata,
      },
    }
  }

  return addApolloState(apolloClient, {
    props: props,
  })
}

export default SingleRead
